import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { GlowPanel } from "./GlowPanel";
// The pager buttons and the place line are the board's, so the two read as one control.
import "./paged-board.css";
import "./paged-reader.css";

type Props = {
  /** Names what is being read for a screen reader, e.g. the post's title. */
  label: string;
  children: ReactNode;
};

const GAP_PX = 48;

/**
 * Long text that never scrolls. The text is laid out in columns exactly one window wide, as
 * an e-reader does, and the reader steps through them a page at a time with the buttons or
 * the arrow keys. All of the text stays in the document, so it can be searched, selected
 * and read by a screen reader or a search engine as one piece.
 */
export function PagedReader({ label, children }: Props) {
  const frame = useRef<HTMLDivElement>(null);
  const flow = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState(1);
  const [pageWidth, setPageWidth] = useState(0);

  useEffect(() => {
    const frameElement = frame.current;
    const flowElement = flow.current;
    if (!frameElement || !flowElement) return;
    const measure = () => {
      const width = frameElement.clientWidth;
      if (width === 0) return;
      setPageWidth(width);
      const count = Math.max(1, Math.round((flowElement.scrollWidth + GAP_PX) / (width + GAP_PX)));
      setPages(count);
      setPage((current) => Math.min(current, count - 1));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(frameElement);
    observer.observe(flowElement);
    return () => observer.disconnect();
  }, []);

  const turn = (by: number) => {
    setPage((current) => Math.min(Math.max(current + by, 0), pages - 1));
  };

  // The arrow keys turn the page from anywhere, as they would scroll a page that scrolled,
  // except while the visitor is typing or has a dialog open.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      if (document.querySelector("dialog[open]")) return;
      if (event.target instanceof HTMLElement && event.target.closest("input, textarea, select")) {
        return;
      }
      if (event.key === "ArrowRight") setPage((current) => Math.min(current + 1, pages - 1));
      if (event.key === "ArrowLeft") setPage((current) => Math.max(current - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [pages]);

  // A link on a page that is not showing can still take the keyboard's focus. When it does,
  // turn to its page, and undo the sideways scroll the browser made to reveal it.
  useEffect(() => {
    const frameElement = frame.current;
    const flowElement = flow.current;
    if (!frameElement || !flowElement || pageWidth === 0) return;
    const onFocus = (event: FocusEvent) => {
      if (!(event.target instanceof HTMLElement)) return;
      const offset =
        event.target.getBoundingClientRect().left - flowElement.getBoundingClientRect().left;
      frameElement.scrollLeft = 0;
      setPage(Math.min(Math.max(Math.floor(offset / (pageWidth + GAP_PX)), 0), pages - 1));
    };
    frameElement.addEventListener("focusin", onFocus);
    return () => {
      frameElement.removeEventListener("focusin", onFocus);
    };
  }, [pageWidth, pages]);

  return (
    <GlowPanel variant="feature" className="paged-reader">
      <section className="paged-reader__layout" aria-label={label}>
        <div ref={frame} className="paged-reader__frame">
          <div
            ref={flow}
            className="paged-reader__flow"
            style={
              {
                "--reader-page": `${String(pageWidth)}px`,
                "--reader-gap": `${String(GAP_PX)}px`,
                "--reader-shift": `${String(-page * (pageWidth + GAP_PX))}px`,
              } as CSSProperties
            }
          >
            {children}
          </div>
        </div>
        {pages > 1 ? (
          <nav className="paged-reader__pager" aria-label={`${label}: pages`}>
            <button
              type="button"
              className="paged-board__step"
              onClick={() => {
                turn(-1);
              }}
              disabled={page === 0}
            >
              <span aria-hidden="true">&larr;&nbsp;</span>
              Back
            </button>
            <p className="paged-board__place" role="status">
              Page {page + 1} of {pages}
            </p>
            <button
              type="button"
              className="paged-board__step"
              onClick={() => {
                turn(1);
              }}
              disabled={page === pages - 1}
            >
              Next
              <span aria-hidden="true">&nbsp;&rarr;</span>
            </button>
          </nav>
        ) : null}
      </section>
    </GlowPanel>
  );
}
